import React, {ReactNode, useMemo} from 'react';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useToggle} from '../../../../../../srcCommon/helpers/hookHelpers';
import {useRuleEditorContext} from '../../../../editors/ruleEditor/ruleEditorContext';
import {ClientRuleAction, RuleActionTypesEnum, TagAction} from '../../../ruleActionModel';
import {EditorStateActions} from '../../hooks/useEditorState';
import {RuleActionCardProps} from '../../ruleActionCard';
import {RuleActionsList} from '../../ruleActionsList';
import {useRuleActionGroups} from '../../ruleEditorActions';
import {RuleEditorSection} from '../../ruleEditorSection';
import {useRuleActionWithParams} from '../helpers/recipeHooksHelpers';
import {buildRuleActionCardsProps} from '../helpers/recipesHelpers';

/*
 * Props.
 */

interface SlaEditorActionsProps {
  actions: ReadonlyArray<ClientRuleAction>;
  description: ReactNode;
  tagId: number | undefined;

  onAddAction: EditorStateActions['onAddAction'];
  onUpdateAction: EditorStateActions['onUpdateAction'];
  onDeleteAction: EditorStateActions['onDeleteAction'];
  onValidation: EditorStateActions['onValidation'];

  onSetTag: HandlerOf<number | undefined>;
}

/*
 * Component.
 */

export const SlaEditorActions: React.FC<SlaEditorActionsProps> = (props) => {
  const {description, tagId, onAddAction, onUpdateAction, onDeleteAction, onValidation, onSetTag} = props;

  const {ruleIntegration} = useRuleEditorContext();
  const actionGroups = useRuleActionGroups(ruleIntegration);

  const [shouldShowMoreOptions, toggleMoreOptions] = useToggle();

  const tagParams = useMemo(() => ({tag: tagId}), [tagId]);
  const tagAction = useRuleActionWithParams<TagAction>(RuleActionTypesEnum.TAG, tagParams);

  const tagActionProps: RuleActionCardProps = {
    action: tagAction,
    actionDefinition: ruleIntegration.findActionDefinition(tagAction),
    onValidation,
    onUpdateAction: (action) => {
      if (action.type !== RuleActionTypesEnum.TAG) {
        return;
      }

      onSetTag(action.params.tag);
    },
    shouldHideError: false,
  };

  const actions = buildRuleActionCardsProps(
    props.actions,
    ruleIntegration,
    onUpdateAction,
    onValidation,
    onDeleteAction,
  );

  return (
    <RuleEditorSection
      description={description}
      shouldShowMoreOptions={shouldShowMoreOptions}
      toggleMoreOptions={toggleMoreOptions}
    >
      <RuleActionsList
        actions={[tagActionProps, ...actions]}
        actionGroups={actionGroups}
        onAddAction={onAddAction}
        shouldShowMoreOptions={shouldShowMoreOptions}
      />
    </RuleEditorSection>
  );
};
